import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Send,
  Copy,
  Crown,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  History,
  ShoppingCart,
} from "lucide-react";

import { shortAddress } from "@/lib/mockData";
import { showError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/useUserStore";
import {
  C,
  DepositDialog,
  WithdrawDialog,
  TransferDialog,
  BuyFUSDDialog,
  BillDialog,
  AifxHistoryDialog,
} from "@/components/modal/profile";
import { useBalanceV2 } from "@/hooks/api/useBalanceV2";
import { cn, formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";
import useFusdtPrice from "@/hooks/api/useFusdPrice";
import { useUserInviteLevel } from "@/hooks/api/useUserInviteLevel";
import {
  getTokenName,
  INTEGRAL_COIN_CONTRACT,
  PROFILE_OPEN_DEPOSIT_SESSION_KEY,
} from "@/config/common";
import { useGetUserCexPnlSummary } from "@/hooks/api/useGetUserCexPnlSummary";

// ===== 主页面 =====
export default function Profile() {
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const { getTokenBalanceData, data: balanceV2Data } = useBalanceV2();
  const { data: fusdtPrice } = useFusdtPrice();
  const { data: userInviteLevelData } = useUserInviteLevel();
  const { data: cexPnlSummaryData } = useGetUserCexPnlSummary();
  const todayPnl = cexPnlSummaryData?.todayPnl ?? "0";
  const todayPnlValue = Number(todayPnl);
  const isTodayProfit = Number.isFinite(todayPnlValue)
    ? todayPnlValue >= 0
    : true;
  const todayPnlSign = isTodayProfit ? "+" : "-";
  const usdtBalanceData = getTokenBalanceData(USDT_ADDRESS);
  const fusdBalanceData = getTokenBalanceData(CCC_FUSD_ADDRESS);
  const tskxBalanceData = getTokenBalanceData(INTEGRAL_COIN_CONTRACT);
  const tskToUsdtBalanceData = balanceV2Data?.tskWaitExchangeValueUsdt ?? "0";
  const totalUsdt =
    +formatEtherWithDecimals(BigInt(usdtBalanceData?.total ?? 0)) +
    +formatEtherWithDecimals(BigInt(fusdBalanceData?.total ?? 0)) *
      (fusdtPrice ?? 0) +
    formatEtherWithDecimals(BigInt(tskToUsdtBalanceData));
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [buyFusdOpen, setBuyFusdOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  const [billType, setBillType] = useState<"充值" | "提现" | "划转" | "购买">(
    "充值"
  );
  const [aifxHistoryOpen, setAifxHistoryOpen] = useState(false);
  useEffect(() => {
    const shouldAutoOpenDeposit =
      sessionStorage.getItem(PROFILE_OPEN_DEPOSIT_SESSION_KEY) === "1";
    if (!shouldAutoOpenDeposit) return;
    sessionStorage.removeItem(PROFILE_OPEN_DEPOSIT_SESSION_KEY);
    setDepositOpen(true);
  }, []);

  const openBill = (type: "充值" | "提现" | "划转" | "购买") => {
    setBillType(type);
    setBillOpen(true);
  };
  return (
    <div
      className="pt-top-header pb-20 page-enter"
      style={{ background: C.bg }}
    >
      {/* ===== 头部：头像 + 地址 + 等级 ===== */}
      <div
        className="px-4 pt-5 pb-4"
        style={{ background: "#0d1117", borderBottom: `1px solid ${C.border}` }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg"
            style={{
              background: "rgba(34,197,94,0.12)",
              border: "1px solid rgba(34,197,94,0.30)",
              color: C.primary,
            }}
          >
            {shortAddress(userInfo?.address ?? "")
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span
                className="mono-num text-sm font-semibold"
                style={{ color: C.text }}
              >
                {shortAddress(userInfo?.address ?? "")}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(userInfo?.address ?? "");
                  showSuccess(t("profilePage.addressCopied"));
                }}
              >
                <Copy size={12} style={{ color: C.dim }} />
              </button>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                style={{
                  background: "rgba(34,197,94,0.10)",
                  border: "1px solid rgba(34,197,94,0.25)",
                  color: C.primary,
                }}
              >
                <Crown size={10} />
                {userInviteLevelData?.level}
              </span>
            </div>
          </div>
        </div>

        {/* 总资产 + 今日盈亏 */}
        <div className="grid grid-cols-2 gap-3">
          <div
            className="rounded-lg p-3"
            style={{ background: C.card, border: `1px solid ${C.border}` }}
          >
            <div className="text-xs mb-1" style={{ color: C.dim }}>
              {t("profilePage.totalAssets")}
            </div>
            <div
              className="mono-num font-bold text-base"
              style={{ color: C.text }}
            >
              ${formatThousands(totalUsdt)}{" "}
            </div>
          </div>
          <div
            className="rounded-lg p-3"
            style={{ background: C.card, border: `1px solid ${C.border}` }}
          >
            <div className="text-xs mb-1" style={{ color: C.dim }}>
              {t("profilePage.todayPnl")}
            </div>
            {/* TODO: 获取今日盈亏 */}
            <div
              className="mono-num font-bold text-base flex items-center gap-1"
              style={{ color: isTodayProfit ? C.green : C.red }}
            >
              {isTodayProfit ? (
                <TrendingUp size={13} />
              ) : (
                <TrendingDown size={13} />
              )}
              {todayPnlSign}$
              {formatThousands(Math.abs(todayPnlValue).toFixed(2))}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* ===== 操作按钮 ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-4 gap-2"
        >
          <button
            onClick={() => setDepositOpen(true)}
            // onClick={() => showError(t("toast.common.featureNotAvailable"))}
            className="flex flex-col items-center gap-2 py-3 rounded-lg transition-all hover:opacity-80 active:scale-95"
            style={{
              background: "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.20)",
            }}
          >
            <span style={{ color: C.green }}>
              <ArrowDownLeft size={18} />
            </span>
            <span
              className="text-[11px] font-semibold"
              style={{ color: C.green }}
            >
              {t("profilePage.deposit")}
            </span>
          </button>

          <button
            onClick={() => setWithdrawOpen(true)}
            // onClick={() => showError(t("toast.common.featureNotAvailable"))}
            className="flex flex-col items-center gap-2 py-3 rounded-lg transition-all hover:opacity-80 active:scale-95"
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.20)",
            }}
          >
            <span style={{ color: C.red }}>
              <ArrowUpRight size={18} />
            </span>
            <span
              className="text-[11px] font-semibold"
              style={{ color: C.red }}
            >
              {t("profilePage.withdraw")}
            </span>
          </button>

          <button
            // onClick={() => setTransferOpen(true)}
            onClick={() => showError(t("toast.common.featureNotAvailable"))}
            className="flex flex-col items-center gap-2 py-3 rounded-lg transition-all hover:opacity-80 active:scale-95"
            style={{
              background: "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.20)",
            }}
          >
            <span style={{ color: C.green }}>
              <Send size={18} />
            </span>
            <span
              className="text-[11px] font-semibold"
              style={{ color: C.green }}
            >
              {t("profilePage.transfer")}
            </span>
          </button>
          {/* todo: 购买FUSD按钮 暂时关闭 */}
          {userInfo?.canMix && false && (
            <button
              onClick={() => setBuyFusdOpen(true)}
              className="flex flex-col items-center gap-2 py-3 rounded-lg transition-all hover:opacity-80 active:scale-95"
              style={{
                background: "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.20)",
              }}
            >
              <span style={{ color: C.amber }}>
                <ShoppingCart size={18} />
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: C.amber }}
              >
                {t("profilePage.buyFusd", {
                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                })}
              </span>
            </button>
          )}
        </motion.div>

        {/* ===== 资产明细 ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-lg overflow-hidden"
          style={{ background: C.card, border: `1px solid ${C.border}` }}
        >
          <div
            className="px-4 py-3 flex items-center gap-2"
            style={{ borderBottom: `1px solid ${C.border}` }}
          >
            <TrendingUp size={14} style={{ color: C.primary }} />
            <span className="font-semibold text-sm" style={{ color: C.text }}>
              {t("profilePage.assets.title")}
            </span>
          </div>

          <div className="divide-y" style={{ borderColor: C.border }}>
            {/* USDT */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div
                    className="text-sm font-semibold"
                    style={{ color: C.text }}
                  >
                    {getTokenName(USDT_ADDRESS)}
                  </div>
                  <div className="text-xs" style={{ color: C.dim }}>
                    {t("profilePage.assets.totalAmountUsdt", {
                      value: formatThousands(
                        formatEtherWithDecimals(usdtBalanceData?.total ?? 0)
                      ),
                      tokenNameUsdt: getTokenName(USDT_ADDRESS),
                    })}
                  </div>
                </div>
                <div
                  className="mono-num font-bold text-base"
                  style={{ color: C.text }}
                >
                  {formatThousands(
                    formatEtherWithDecimals(usdtBalanceData?.total ?? 0)
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {[
                  {
                    label: t("profilePage.assets.available"),
                    value: formatThousands(
                      formatEtherWithDecimals(
                        usdtBalanceData?.canUseUsdtAmount ?? 0
                      )
                    ),
                  },
                  {
                    label: t("profilePage.assets.custodyFrozen"),
                    value: formatThousands(
                      formatEtherWithDecimals(
                        usdtBalanceData?.tuoGuanFreezeUsdtAmount ?? 0
                      )
                    ),
                  },
                  {
                    label: t("profilePage.assets.tradingFrozen"),
                    value: formatThousands(
                      formatEtherWithDecimals(
                        usdtBalanceData?.ceLvFreezeUsdtAmount ?? 0
                      )
                    ),
                  },
                ].map(item => (
                  <div
                    key={item.label}
                    className="rounded-lg p-2"
                    style={{
                      background: C.card2,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div style={{ color: C.dim }}>{item.label}</div>
                    <div
                      className="mono-num font-semibold mt-1"
                      style={{ color: C.muted }}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FUSD */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div
                    className="text-sm font-semibold"
                    style={{ color: C.text }}
                  >
                    {getTokenName(CCC_FUSD_ADDRESS)}
                  </div>
                  <div className="text-xs" style={{ color: C.dim }}>
                    {t("profilePage.assets.totalAmount", {
                      value: formatThousands(
                        formatEtherWithDecimals(fusdBalanceData?.total ?? 0)
                      ),
                      tokenName: getTokenName(CCC_FUSD_ADDRESS),
                    })}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="mono-num font-bold text-base"
                    style={{ color: C.primary }}
                  >
                    $
                    {formatThousands(
                      +formatEtherWithDecimals(fusdBalanceData?.total ?? 0) *
                        (fusdtPrice ?? 0)
                    )}
                  </div>
                  <div className="text-xs" style={{ color: C.dim }}>
                    @${fusdtPrice?.toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  {
                    label: t("profilePage.assets.gameBalance", {
                      tokenName: getTokenName(CCC_FUSD_ADDRESS),
                    }),
                    value: formatThousands(
                      formatEtherWithDecimals(
                        fusdBalanceData?.gameFusdAmount ?? 0
                      )
                    ),
                  },
                  {
                    label: t("profilePage.assets.pendingWithdrawal", {
                      tokenName: getTokenName(CCC_FUSD_ADDRESS),
                    }),
                    value: formatThousands(
                      formatEtherWithDecimals(
                        fusdBalanceData?.waitClaimFusdAmount ?? 0
                      )
                    ),
                  },
                ].map(item => (
                  <div
                    key={item.label}
                    className="rounded-lg p-2"
                    style={{
                      background: C.card2,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div style={{ color: C.dim }}>{item.label}</div>
                    <div
                      className="mono-num font-semibold mt-1"
                      style={{ color: C.muted }}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AIF */}
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <div
                  className="text-sm font-semibold"
                  style={{ color: C.text }}
                >
                  {getTokenName(CCC_AIF_ADDRESS)}
                </div>
                <div className="text-xs" style={{ color: C.dim }}>
                  {t("profilePage.assets.aifBalance", {
                    value: "-",
                    tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                  })}
                </div>
              </div>
              <div className="text-right">
                <div
                  className="mono-num font-bold text-sm"
                  style={{ color: C.primary }}
                >
                  ${formatEtherWithDecimals(BigInt(tskToUsdtBalanceData))}
                </div>
                {/* <div className="text-xs" style={{ color: C.dim }}>
                  @${MOCK_ASSETS.aif.price}
                </div> */}
              </div>
            </div>

            {/* AIFx */}
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <div
                  className="text-sm font-semibold"
                  style={{ color: C.text }}
                >
                  {t("profilePage.assets.aifxPoints", {
                    tokenNameAifx: getTokenName(INTEGRAL_COIN_CONTRACT),
                  })}
                </div>
                {/* <div className="text-xs" style={{ color: C.dim }}>
                  {t("profilePage.assets.exchangeCountdown", {
                    time: MOCK_ASSETS.aifx.nextExchangeIn,
                  })}
                </div> */}
              </div>
              <div className="text-right">
                <div
                  className="mono-num font-bold text-sm"
                  style={{ color: C.amber }}
                >
                  {formatThousands(
                    formatEtherWithDecimals(tskxBalanceData?.total ?? 0)
                  )}
                </div>
                {/* <button
                  onClick={() => setAifxHistoryOpen(true)}
                  className="text-xs mt-0.5"
                  style={{ color: C.primary }}
                >
                  {t("profilePage.assets.exchangeHistory")}
                </button> */}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ===== 账单记录 ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-lg overflow-hidden"
          style={{ background: C.card, border: `1px solid ${C.border}` }}
        >
          <div
            className="px-4 py-3 flex items-center gap-2"
            style={{ borderBottom: `1px solid ${C.border}` }}
          >
            <History size={14} style={{ color: C.primary }} />
            <span className="font-semibold text-sm" style={{ color: C.text }}>
              {t("profilePage.bills.title")}
            </span>
          </div>
          <div className="divide-y" style={{ borderColor: C.border }}>
            {[
              {
                type: "充值" as const,
                label: t("profilePage.bills.deposit"),
                icon: <ArrowDownLeft size={15} />,
                color: C.green,
                bg: "rgba(34,197,94,0.08)",
              },
              {
                type: "提现" as const,
                label: t("profilePage.bills.withdraw"),
                icon: <ArrowUpRight size={15} />,
                color: C.red,
                bg: "rgba(239,68,68,0.08)",
              },
              {
                type: "划转" as const,
                label: t("profilePage.bills.transfer"),
                icon: <Send size={15} />,
                color: C.green,
                bg: "rgba(34,197,94,0.08)",
              },
              // todo: 购买按钮 暂时关闭
              // {
              //   type: "购买" as const,
              //   label: t("profilePage.bills.purchase"),
              //   icon: <ShoppingCart size={15} />,
              //   color: C.amber,
              //   bg: "rgba(245,158,11,0.08)",
              // },
            ].map(item => (
              <button
                key={item.type}
                onClick={() => openBill(item.type)}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-3 transition-all hover:opacity-80"
                  // item.type === "购买" && !userInfo?.canMix ? "hidden" : ""
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: item.bg }}
                  >
                    <span style={{ color: item.color }}>{item.icon}</span>
                  </div>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: C.text }}
                  >
                    {item.label}
                  </span>
                </div>
                <ChevronRight size={14} style={{ color: C.dim }} />
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ===== 弹窗 ===== */}
      <DepositDialog open={depositOpen} onClose={() => setDepositOpen(false)} />
      <WithdrawDialog
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        usdtBalanceData={usdtBalanceData ?? undefined}
      />
      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      />
      <BuyFUSDDialog open={buyFusdOpen} onClose={() => setBuyFusdOpen(false)} />
      <BillDialog
        open={billOpen}
        onClose={() => setBillOpen(false)}
        type={billType}
      />

      {/* AIFx 兑换历史 */}
      <AifxHistoryDialog
        open={aifxHistoryOpen}
        onClose={() => setAifxHistoryOpen(false)}
      />
    </div>
  );
}
